/**
 * hc-drawer-patch.js   (loads after hc-fixes.js)
 *
 * PROBLEM
 * After adding a product from the cart drawer upsell, the Checkout button and
 * the X close button stopped responding.
 *
 * CAUSE
 * header-custom.liquid drives the drawer's open state with a `data-open`
 * ATTRIBUTE, not a class:
 *
 *     .hc-cart-drawer            { transform: translateX(100%); pointer-events: none; }
 *     .hc-cart-drawer[data-open] { transform: translateX(0);    pointer-events: auto; }
 *
 * Refreshing the drawer after the add dropped `data-open`. The drawer stayed
 * on screen but every element inside it inherited `pointer-events: none`, so
 * nothing could be clicked - not the X, not Checkout. Verified by walking the
 * ancestors of the close button: `.hc-cart-drawer pe=none`.
 *
 * FIX
 * Remember whether the drawer was open, refresh once, then re-assert
 * `data-open` on both the drawer and its overlay.
 */
(function () {
  'use strict';

  var DRAWER = '.hc-cart-drawer';
  var OVERLAY = '.hc-cart-drawer-overlay';

  function drawer() { return document.querySelector(DRAWER); }
  function overlay() { return document.querySelector(OVERLAY); }

  function isOpen() {
    var d = drawer();
    return !!(d && d.hasAttribute('data-open'));
  }

  /** Force the drawer back into a genuinely interactive open state. */
  function assertOpen() {
    var d = drawer();
    if (!d) return;
    d.removeAttribute('hidden');
    d.setAttribute('data-open', '');
    var o = overlay();
    if (o) {
      o.removeAttribute('hidden');
      o.setAttribute('data-open', '');
    }
  }

  function api() {
    return (typeof window.OMCart === 'object' && window.OMCart) ? window.OMCart : null;
  }

  var busy = false;

  /**
   * Refresh the drawer contents, preserving the open state.
   * Deliberately ONE refresh, not several - overlapping refreshes were racing
   * each other and were part of why the state kept getting lost.
   */
  function refreshDrawer() {
    if (busy) return Promise.resolve();
    busy = true;

    var wasOpen = isOpen();
    var a = api();
    var run;

    try {
      if (a && typeof a.refresh === 'function') run = a.refresh();
      else if (typeof window.refreshDawnCartUI === 'function') run = window.refreshDawnCartUI();
    } catch (e) { run = null; }

    return Promise.resolve(run)
      .catch(function () {})
      .then(function () {
        // The refresh is async inside the theme; re-assert on the next few
        // frames so we win regardless of when it finishes writing.
        if (wasOpen) {
          assertOpen();
          setTimeout(assertOpen, 120);
          setTimeout(assertOpen, 400);
          setTimeout(assertOpen, 900);
        }
        busy = false;
      });
  }

  window.hcRefreshDrawer = refreshDrawer;

  // Upsell adds: hc-fixes.js performs the POST, we repaint once afterwards.
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-hcx-add]')) return;
    setTimeout(refreshDrawer, 550);
  }, true);

  // Combo builder adds should open the drawer on success.
  document.addEventListener('hamper-builder:added', function () {
    setTimeout(function () {
      var a = api();
      try {
        if (a && typeof a.refreshAndOpen === 'function') a.refreshAndOpen();
        else refreshDrawer();
      } catch (err) { refreshDrawer(); }
      setTimeout(assertOpen, 400);
    }, 250);
  });

  /**
   * Safety net: if the drawer is ever left visible but inert, restore it.
   * Cheap attribute-only observer, no layout reads.
   */
  var d0 = drawer();
  if (d0) {
    new MutationObserver(function () {
      var d = drawer();
      if (!d) return;
      var visible = !d.hasAttribute('hidden');
      var interactive = d.hasAttribute('data-open');
      if (visible && !interactive && d.getBoundingClientRect().width > 0) {
        // On screen but unclickable - the exact broken state. Put it right.
        assertOpen();
      }
    }).observe(d0, { attributes: true, attributeFilter: ['data-open', 'hidden'] });
  }
})();
